import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { useRouter } from '@bprogress/next';
import omit from 'lodash/omit';

import type { selectCalcMainValues } from '@/containers/VoyagePlanner/Summary/FareBreakdown/hooks/calcMainValuesSelector';
import type { AddedAddOn, AddedAddOns } from '@/ducks/addOns/types';
import type { TAddOnPrice, TBookingPriceDetail } from '@/infra/types/cabin';

import { useGetAppliedFareClass } from '@/containers/VoyagePlanner/Summary/SummaryDetails/hooks/useAppliedFareClassInfo';
import { selectAccessKeyPromoCode, selectAccessKeysIsDataReady } from '@/ducks/accessKeys';
import { selectCurrencyCode, selectSailors } from '@/ducks/filters/selectors';
import { selectAgencyId, selectAgentBookingChannel, selectAgentId } from '@/ducks/fm/selectors';
import { selectMnvvReservationNumber } from '@/ducks/mnvv/selectors';
import { selectIsAccessibleCabinAvailable } from '@/ducks/pages/chooseVoyage/selectors';
import {
  selectAddedAddOns,
  selectBookingPriceDetails,
  selectCalculateInvoiceData,
  selectFullPayPrice,
  selectGuestPriceDetails,
  selectServiceChargeAmount,
  selectTripInsuranceAmount,
} from '@/ducks/pages/summary/selectors';
import { selectHasIncludedGratuities, selectIsVoyageProtectionAdded } from '@/ducks/travelParty/selectors';
import useGetChosenSubCategoryCode from '@/ducks/voyageInfo/category/useGetChosenSubCategoryCode';
import { selectSailingDataVoyageId } from '@/ducks/voyageInfo/sailing/selectors';
import checkFlowType from '@/features/flowType/checkFlowType';
import {
  GQL_QUERY_ADDON_AVAILABILITY,
  type TAddOn,
  type TAddonAvailabilityRequest,
  type TAddonAvailabilityResponse,
  type TSortableAddOn,
} from '@/helpers/api/graphql/queries/AddonAvailability';
import roundUpTwoDecimals from '@/helpers/money/roundUpTwoDecimals';
import { getSessionStorageValue, setSessionStorageValue } from '@/helpers/util';
import useVWOFeature, { useVWOFlag, VWOFlag } from '@/hooks/useVWOFeature';
import { type RootState, useAppDispatch, useAppSelector } from '@/store';

import {
  ADD_ONS_SEARCH_PARAM,
  ADDED_ADDONS_SESSION_KEY,
  ADDONS_ADDED_TO_INVOICE_BY_DEFAULT,
  ADDONS_WITHOUT_QUANTITIES,
  SERVICE_CHARGE_ADD_ON_CODE,
  VOYAGE_PROTECTION_ADD_ON_CODE,
} from './constants';
import {
  checkIfAddOnAddedToSessionStorage,
  deserializeData,
  getAddOnPrice,
  getAddOnPriceFromCalcInvoiceData,
  getAddOnsTotalPrice,
  isAdded,
  runAddOnsActions,
  saveInSearchParams,
  serializeData,
  sortByPriority,
} from './utils';

export const useGetAddOnsData = ({ disabled = false }: { disabled?: boolean } = {}) => {
  const searchParams = useSearchParams();
  const isAddOnsEnabled = useVWOFlag(VWOFlag.ADDONS);

  const fareClass = useGetAppliedFareClass();
  const categoryCode = useGetChosenSubCategoryCode();
  const currencyCode = useAppSelector(selectCurrencyCode);
  const isAccessible = useAppSelector(selectIsAccessibleCabinAvailable);
  const sailorsCount = useAppSelector(selectSailors);
  const voyageId = useAppSelector(selectSailingDataVoyageId);
  const accessKey = useAppSelector(selectAccessKeyPromoCode);
  const accessKeysIsDataReady = useAppSelector(selectAccessKeysIsDataReady);
  const agencyId = useAppSelector(selectAgencyId);
  const agentId = useAppSelector(selectAgentId);
  const bookingChannel = useAppSelector(selectAgentBookingChannel);
  const { isFM, isMNVV } = checkFlowType(searchParams);
  const reservationNumber = useAppSelector(selectMnvvReservationNumber);

  const loadConditions = {
    accessKeysIsDataReady,
    hasBaseParams: !disabled && categoryCode && currencyCode && voyageId,
    hasFMParams: !isFM || (agencyId && agentId && bookingChannel),
    hasMNVVParams: !isMNVV || reservationNumber,
    isEnabled: isAddOnsEnabled,
  };

  const doLoad = Object.values(loadConditions).every(Boolean);

  const { data, error, loading, ...rest } = useQuery<TAddonAvailabilityResponse, TAddonAvailabilityRequest>(
    GQL_QUERY_ADDON_AVAILABILITY,
    {
      skip: !doLoad,
      variables: {
        value: {
          accessKeys: accessKey ? [accessKey] : undefined,
          agent: agencyId && agentId && bookingChannel ? { agencyId, agentId, bookingChannel } : undefined,
          cabins: [
            {
              cabinSeqNo: 1,
              categoryCode,
              fareClass,
              isAccessible,
              travelParty: [{ ageCategory: 'ADULT', count: sailorsCount }],
            },
          ],
          currencyCode,
          reservationNumber,
          voyageId,
        },
      },
    },
  );

  const [addOns, addOnsMap] = useMemo(() => {
    const addOnsMap =
      data?.addOnAvailability?.[0]?.addOns.reduce<Record<string, TAddOn>>((addOns, rawAddOn) => {
        const patchedAddOn = {
          ...rawAddOn,
          // @ts-expect-error TODO remove it once BE is fixed https://virginvoyages.atlassian.net/browse/MSH-134360?focusedCommentId=1114005
          bookingSortPriority: rawAddOn.bookingSortPriority && parseInt(rawAddOn.bookingSortPriority),
          // @ts-expect-error TODO remove it once BE is fixed https://virginvoyages.atlassian.net/browse/MSH-134360?focusedCommentId=1114005
          hideOnAddonPage: rawAddOn.hideOnAddonPage && rawAddOn.hideOnAddonPage !== 'false',
          // @ts-expect-error TODO remove it once BE is fixed https://virginvoyages.atlassian.net/browse/MSH-134360?focusedCommentId=1114005
          maximumQuantity: rawAddOn.maximumQuantity && parseInt(rawAddOn.maximumQuantity),
        };

        const addOn: TAddOn =
          patchedAddOn.maximumQuantity && patchedAddOn.maximumQuantity > 0
            ? { ...patchedAddOn, nestedAddOns: [patchedAddOn] }
            : patchedAddOn;

        return !rawAddOn.addOnDetails ? addOns : { ...addOns, [addOn.code]: addOn };
      }, {}) || {};

    return [
      Object.keys(addOnsMap)
        .map((code) => addOnsMap[code]!)
        ?.sort(sortByPriority),
      addOnsMap,
    ];
  }, [data]);

  return {
    ...rest,
    addOnsMap,
    data: addOns,
    error,
    loaded: typeof data !== 'undefined' || error,
    loading: !!doLoad && (loading || !addOns) && !error,
  };
};

const INITIAL_ARRAY: AddedAddOn[] = [];

export const useAddOnsManager = ({
  processAddedAddOns,
}: { processAddedAddOns?: (addOns: AddedAddOn[]) => AddedAddOn[] } = {}) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const [addedAddOns, setAddedAddOns] = useState<AddedAddOns>();

  const dataProps = useGetAddOnsData();
  const { addOnsMap, data: addOns, loaded } = dataProps;

  useEffect(() => {
    if (loaded) {
      const deserializedData = deserializeData(searchParams, addOns);
      setAddedAddOns(deserializedData);
      setSessionStorageValue(ADDED_ADDONS_SESSION_KEY, deserializedData);
    }
  }, [addOns, searchParams, loaded]);

  const add = (values: AddedAddOns) => {
    const newValue = { ...addedAddOns, ...values };
    saveInSearchParams(router, serializeData(newValue));
    setAddedAddOns(newValue);
    runAddOnsActions(dispatch, newValue);
    setSessionStorageValue(ADDED_ADDONS_SESSION_KEY, newValue);
  };

  const remove = (codes: string[]) => {
    const newValue = omit(addedAddOns, codes);
    saveInSearchParams(router, serializeData(newValue));
    setAddedAddOns(newValue);
    runAddOnsActions(dispatch, newValue);
    setSessionStorageValue(ADDED_ADDONS_SESSION_KEY, newValue);
  };

  const setQuantity = (code: string, quantity: number) => {
    if (quantity === 0) {
      remove([code]);
    } else {
      const newValue = { ...addedAddOns, [code]: quantity };
      saveInSearchParams(router, serializeData(newValue));
      setAddedAddOns(newValue);
      runAddOnsActions(dispatch, newValue);
      setSessionStorageValue(ADDED_ADDONS_SESSION_KEY, newValue);
    }
  };

  const addedAddOnsArray = useMemo(() => {
    const addOns = Object.keys(addedAddOns || {})
      .reduce(
        (addedAddOnsArray, code) =>
          addedAddOns && addedAddOns[code]! > 0 && addOnsMap[code]
            ? [...addedAddOnsArray, { ...addOnsMap[code]!, quantity: addedAddOns[code]! }]
            : addedAddOnsArray,
        INITIAL_ARRAY,
      )
      .sort(sortByPriority);

    return processAddedAddOns ? processAddedAddOns(addOns) : addOns;
  }, [addOnsMap, addedAddOns, processAddedAddOns]);

  const addOnsForAddOnsPage = {
    addedAddOns: addedAddOnsArray.filter(({ hideOnAddonPage }) => !hideOnAddonPage),
    addOns: addOns.filter(({ hideOnAddonPage }) => !hideOnAddonPage),
  };

  return {
    ...dataProps,
    add,
    addedAddOns: addedAddOnsArray,
    addOns,
    addOnsForAddOnsPage,
    getAddOnsTotalPrice: () => getAddOnsTotalPrice(addOnsForAddOnsPage.addedAddOns, addedAddOns),
    getSerializedData: () => serializeData(addedAddOns),
    isAdded: (addOn?: TAddOn) => isAdded(addedAddOns, addOn),
    isAddedByCode: (code: string) => isAdded(addedAddOns, { code } as TAddOn),
    loaded: typeof addedAddOns !== 'undefined' && loaded,
    remove,
    setQuantity,
  };
};

export const useAddOnsValues = (addOn: TAddOn) => {
  const searchParams = useSearchParams();
  const { data } = useGetAddOnsData({ disabled: !searchParams.get(ADD_ONS_SEARCH_PARAM) });

  return useMemo(() => {
    const initialValues = addOn.nestedAddOns?.reduce<Record<string, number>>(
      (initialValues, nestedAddOn) => ({
        ...initialValues,
        [nestedAddOn.code]: 0,
      }),
      {},
    ) || { [addOn.code]: 0 };

    const values = {
      ...initialValues,
      ...deserializeData(searchParams, data, addOn.nestedAddOns?.map(({ code }) => code) || [addOn.code]),
    };

    return values;
  }, [addOn, searchParams, data]);
};

// Hooks for legacy VP/ServiceCharge support (actual while add-ons behind the flag)

const useIsAddOnEnabled = (
  code: string,
  legacySelector: (state: RootState) => boolean | undefined,
  checkSessionStorage = false,
) => {
  const { addedAddOns } = useAddOnsManager();
  const isAddOnsEnabled = useVWOFlag(VWOFlag.ADDONS);
  const isEnabledLegacy = useAppSelector(legacySelector);

  if (!isAddOnsEnabled) {
    return isEnabledLegacy || false;
  }

  if (checkSessionStorage) {
    const storageValue = getSessionStorageValue(ADDED_ADDONS_SESSION_KEY)?.[code];
    return storageValue && storageValue > 0;
  }

  const addedAddOn = addedAddOns.find((addOn) => code === addOn.code);
  return (addedAddOn && addedAddOn.quantity > 0) || false;
};

const useIsAddOnAddedViaSearchParams /* (aka "via Add-ons page") */ = (code: string) => {
  const isAddOnsEnabled = useVWOFlag(VWOFlag.ADDONS);
  const searchParams = useSearchParams();

  return useMemo(() => {
    const data = deserializeData(searchParams);
    return isAddOnsEnabled ? !!data[code] : false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddOnsEnabled]);
};

const useGetAddOnPrice = (code: string, legacySelector: (state: RootState) => number) => {
  const isAddOnsEnabled = useVWOFlag(VWOFlag.ADDONS);
  const { data } = useGetAddOnsData();
  const amount = useAppSelector(legacySelector);

  if (!isAddOnsEnabled) {
    return amount;
  }

  const addOn = data.find((addOn) => addOn.code === code);
  return addOn && getAddOnPrice(addOn)?.amount;
};

export const useIsVoyageProtectionAddOnEnabled = (checkSessionStorage = false) =>
  useIsAddOnEnabled(VOYAGE_PROTECTION_ADD_ON_CODE, selectIsVoyageProtectionAdded, checkSessionStorage);

export const useIsServiceChargeAddOnEnabled = (checkSessionStorage = false) =>
  useIsAddOnEnabled(SERVICE_CHARGE_ADD_ON_CODE, selectHasIncludedGratuities, checkSessionStorage);

export const useIsVoyageProtectionAddedViaSearchParams = () =>
  useIsAddOnAddedViaSearchParams(VOYAGE_PROTECTION_ADD_ON_CODE);

export const useIsServiceChargeAddedViaSearchParams = () => useIsAddOnAddedViaSearchParams(SERVICE_CHARGE_ADD_ON_CODE);

export const useGetVoyageProtectionAddOnPrice = () =>
  useGetAddOnPrice(VOYAGE_PROTECTION_ADD_ON_CODE, selectTripInsuranceAmount);

export const useGetServiceChargeAddOnPrice = () =>
  useGetAddOnPrice(SERVICE_CHARGE_ADD_ON_CODE, selectServiceChargeAmount);

export const useGetAddedAddonsForFareBreakdown = ({
  checkSessionStorage = false,
  isPayInFullPromoAppliedWhenAvailable,
}: { checkSessionStorage?: boolean; isPayInFullPromoAppliedWhenAvailable?: boolean } = {}) => {
  const addedAddOns = useAppSelector((state) => selectAddedAddOns(state, isPayInFullPromoAppliedWhenAvailable));
  const { data, isAddedByCode } = useAddOnsManager();

  const checkIfAddedFn = checkSessionStorage ? checkIfAddOnAddedToSessionStorage : isAddedByCode;

  const codeToPriorityMap = data.reduce<Record<string, number | undefined>>(
    (map, addOn) => ({ ...map, [addOn.code]: addOn.bookingSortPriority }),
    {},
  );

  let addOns = addedAddOns
    ?.map<TAddOnPrice & TSortableAddOn>((addOn) => ({
      ...addOn,
      bookingSortPriority: codeToPriorityMap[addOn.code],
      quantity: ADDONS_WITHOUT_QUANTITIES.includes(addOn.code) ? undefined : addOn.quantity,
    }))
    .sort(sortByPriority);

  addOns = addOns?.filter(({ code }) => checkIfAddedFn(code) && code !== SERVICE_CHARGE_ADD_ON_CODE);

  return addOns;
};

export const useGetAddedAddonsForCalcInvoiceCall = () => {
  const { addedAddOns, addOns } = useAddOnsManager();

  const modifiedAddedAddOns = [...addedAddOns];
  ADDONS_ADDED_TO_INVOICE_BY_DEFAULT.forEach((code) => {
    if (addedAddOns.findIndex((addOn) => addOn.code === code) === -1) {
      const addOnToAdd = addOns.find((addOn) => addOn.code === code);
      if (addOnToAdd) {
        modifiedAddedAddOns.push({
          ...addOnToAdd,
          quantity: 1,
        });
      }
    }
  });

  const addedAddOnsDepsKey = serializeData(
    modifiedAddedAddOns.reduce((map, addOn) => ({ ...map, [addOn.code]: addOn.quantity }), {}),
  );

  return {
    addedAddOns: modifiedAddedAddOns,
    addedAddOnsDepsKey,
  };
};

/**
 * Temporary hook to adjust CalcMainValues until add-ons behind the flag and FE manually calculate prices
 * @see https://virginvoyages.atlassian.net/browse/MSH-138434
 */
export const useGetAdjustedMainValues = (props: ReturnType<typeof selectCalcMainValues>) => {
  const { isAddedByCode, loaded } = useAddOnsManager();
  const { isVWOLoading, value: isAddOnsEnabled } = useVWOFeature(VWOFlag.ADDONS);
  const bookingPriceDetails = useAppSelector(selectBookingPriceDetails);
  const fullPayPrice = useAppSelector(selectFullPayPrice);
  const guestPriceDetails = useAppSelector(selectGuestPriceDetails);
  const calculateInvoiceData = useAppSelector(selectCalculateInvoiceData);

  if (!loaded || !isAddOnsEnabled) {
    return {
      ...props,
      isLoading: isVWOLoading || props.isLoading,
    };
  }

  const priceDetails = props.isPayInFullPromoApplied ? fullPayPrice : bookingPriceDetails;

  const totalAmountAdjuster = (priceDetails?: Partial<TBookingPriceDetail>) =>
    priceDetails?.addOnPrices?.reduce(
      (value: number, { amount, code }: TAddOnPrice) =>
        ADDONS_ADDED_TO_INVOICE_BY_DEFAULT.includes(code) && !isAddedByCode(code) ? value + amount : value,
      0,
    ) || 0;

  const sailors = guestPriceDetails?.map((item, index) => {
    return {
      name: `Sailor ${index + 1}`,
      value:
        item.originalAmount -
        item.addOnPrices.reduce((value: number, { amount }: TAddOnPrice) => value + amount, 0) -
        item.taxAmount -
        item.portFees,
    };
  });

  const propsAdjuster = {
    cabin: {
      ...props.cabin,
      items: {
        ...props.cabin?.items,
        sailors,
      },
    },
    grandTotalAmount: roundUpTwoDecimals((priceDetails?.amount || 0) - totalAmountAdjuster(priceDetails)),
    originalGrandTotalAmount: roundUpTwoDecimals(
      (bookingPriceDetails?.amount || 0) - totalAmountAdjuster(bookingPriceDetails),
    ),
    originalTotalAmount: roundUpTwoDecimals(
      (bookingPriceDetails?.originalAmount || 0) - totalAmountAdjuster(bookingPriceDetails),
    ),
    payInFullGrandTotalAmount: roundUpTwoDecimals((fullPayPrice?.amount || 0) - totalAmountAdjuster(fullPayPrice)),
    sailorsAmount: sailors?.reduce((sum, { value }) => sum + value, 0) || 0,
  };

  const depositAmount = calculateInvoiceData.paymentSchedule?.[0]?.depositAmount?.amount || 0;

  return {
    ...props,
    ...propsAdjuster,
    addOnsPrice: getAddOnPriceFromCalcInvoiceData({
      calculateInvoiceData,
      payInFullPromoApplied: props.isPayInFullPromoApplied,
    }),
    cabinPrice: roundUpTwoDecimals(
      (bookingPriceDetails?.amount || 0) -
        (bookingPriceDetails?.addOnPrices?.reduce((total, { amount }) => total + amount, 0) || 0),
    ),
    depositAmount,
    depositAmountWithAddOns:
      depositAmount +
      getAddOnPriceFromCalcInvoiceData({
        calculateInvoiceData,
        payInFullPromoApplied: false,
      }),
    gratuityAmount: isAddedByCode(SERVICE_CHARGE_ADD_ON_CODE)
      ? priceDetails?.addOnPrices?.find(({ code }) => code === SERVICE_CHARGE_ADD_ON_CODE)?.amount || 0
      : 0,
    gratuityTotalAmount:
      priceDetails?.addOnPrices?.find(({ code }) => code === SERVICE_CHARGE_ADD_ON_CODE)?.amount || 0,
    tripInsurance: isAddedByCode(VOYAGE_PROTECTION_ADD_ON_CODE)
      ? priceDetails?.addOnPrices?.find(({ code }) => code === VOYAGE_PROTECTION_ADD_ON_CODE)?.amount || 0
      : 0,
  };
};
